import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import { useCoverPreview, useQueueActions } from '@/features/queue/api';
import type { QueueTicket } from '@/features/queue/types';
import { isApiError } from '@/lib/api';
import styles from './queue.module.css';

/**
 * "Cover check": the patient's panel against today's visit, so nobody finds
 * out at the bill. No panel system is connected yet: the check reads the
 * membership on the record (shown under the result).
 * RL-1664: a membership the patient typed in the app shows a "Card checked"
 * tick; saving with it marks the membership checked (and makes it the payer
 * of a self-pay visit) in one step, so the bill is not self-pay.
 */
export function CoverCheckDrawer({
  ticket,
  onClose,
}: {
  ticket: QueueTicket | null;
  onClose: () => void;
}) {
  const { data, isLoading, error, refetch } = useCoverPreview(
    ticket?.id ?? null,
  );
  const { t } = useTranslation();
  const a = useQueueActions();
  const toast = useToast();
  // RL-1664: one step for a membership the patient typed in the app.
  const [cardChecked, setCardChecked] = useState(false);
  const [memberNo, setMemberNo] = useState('');
  const card = data?.cardCheck ?? null;
  const marking = !!card && cardChecked;
  const blocked = !!card && cardChecked && !card.allowed;
  // Review fix: the API takes 2 to 40 characters (or nothing).
  const memberNoBad = marking && memberNo.trim().length === 1;
  return (
    <Dialog
      open={!!ticket}
      onClose={onClose}
      title={`Cover check · ${ticket?.patient.fullName ?? ''}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={<Check size={16} />}
            disabled={!data || blocked || memberNoBad}
            loading={a.saveCover.isPending}
            onClick={() =>
              ticket &&
              a.saveCover
                .mutateAsync(
                  marking
                    ? {
                        id: ticket.id,
                        cardChecked: true,
                        ...(memberNo.trim()
                          ? { memberNo: memberNo.trim() }
                          : {}),
                      }
                    : ticket.id,
                )
                .then((saved) => {
                  toast.show({
                    tone: 'success',
                    title: marking
                      ? saved.cover?.status === 'covered'
                        ? t('queue.cardCheck.covered', {
                            payer:
                              saved.payer?.name ?? t('queue.cardCheck.panel'),
                          })
                        : t('queue.cardCheck.notCovered')
                      : 'Saved to the visit',
                    body: marking ? t('queue.cardCheck.markedBody') : undefined,
                  });
                  onClose();
                })
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not saved',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Save to the visit
          </Button>
        </>
      }
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && (
        <div className={styles.drawer}>
          <p
            className={
              data.status === 'covered' ? styles.successNote : styles.errorNote
            }
          >
            {data.message}
          </p>
          <dl className={styles.rows}>
            <dt>Member / GL</dt>
            <dd>{data.memberNo ?? '—'}</dd>
            <dt>Yearly limit</dt>
            <dd>
              {data.yearlyLimitSen === null
                ? 'No limit on record'
                : formatRM(data.yearlyLimitSen)}
            </dd>
            <dt>Used</dt>
            <dd>{data.usedSen === null ? '—' : formatRM(data.usedSen)}</dd>
            <dt>Left</dt>
            <dd>{data.leftSen === null ? '—' : formatRM(data.leftSen)}</dd>
            <dt>Co-payment</dt>
            <dd>{data.coPayment ?? 'None'}</dd>
            <dt>Not covered</dt>
            <dd>{data.notCovered ?? '—'}</dd>
          </dl>
          {card && (
            <div className={styles.section} aria-label="Card check">
              <label className={styles.flagLine}>
                <input
                  type="checkbox"
                  checked={cardChecked}
                  onChange={(e) => setCardChecked(e.target.checked)}
                />
                {t('queue.cardCheck.tick')}
              </label>
              {cardChecked && (
                <Field
                  label={t('queue.cardCheck.memberNo')}
                  error={
                    memberNoBad ? t('queue.cardCheck.memberNoShort') : undefined
                  }
                  hint={t(
                    card.setsPayer
                      ? 'queue.cardCheck.hintPayer'
                      : 'queue.cardCheck.hint',
                    {
                      result:
                        card.ifChecked === 'covered'
                          ? t('queue.cardCheck.resultCovered')
                          : t('queue.cardCheck.resultNotCovered'),
                    },
                  )}
                >
                  <Input
                    value={memberNo}
                    maxLength={40}
                    placeholder={data.memberNo ?? ''}
                    onChange={(e) => setMemberNo(e.target.value)}
                  />
                </Field>
              )}
              {blocked && (
                <p className={styles.errorNote} role="alert">
                  {t('queue.cardCheck.notAllowed')}
                </p>
              )}
            </div>
          )}
          <p className={styles.sub}>Source: {data.source}.</p>
        </div>
      )}
    </Dialog>
  );
}
